// admin-web/src/firebase.ts
import { getApps, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from "firebase/app-check";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";

/**
 * Firebase Auth reads navigator.userAgent very early.
 * In some hardened contexts, globalThis.navigator can be null.
 * This prevents a hard crash and lets the app render.
 */
function ensureNavigator() {
  try {
    const g: any = globalThis as any;
    const nav = g.navigator;
    if (!nav) {
      g.navigator = { userAgent: "admin-web" };
      return;
    }
    if (typeof nav.userAgent !== "string") {
      nav.userAgent = "admin-web";
    }
  } catch {
    // ignore
  }
}

ensureNavigator();

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

function assertEnv(name: string, value: any) {
  if (!value) {
    // eslint-disable-next-line no-console
    console.warn(`[firebase] Missing env var: ${name}`);
  }
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

assertEnv("VITE_FIREBASE_API_KEY", firebaseConfig.apiKey);
assertEnv("VITE_FIREBASE_AUTH_DOMAIN", firebaseConfig.authDomain);
assertEnv("VITE_FIREBASE_PROJECT_ID", firebaseConfig.projectId);
assertEnv("VITE_FIREBASE_STORAGE_BUCKET", firebaseConfig.storageBucket);
assertEnv("VITE_FIREBASE_MESSAGING_SENDER_ID", firebaseConfig.messagingSenderId);
assertEnv("VITE_FIREBASE_APP_ID", firebaseConfig.appId);

export const app = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig);

function initAppCheck(): AppCheck | null {
  const siteKey = String(import.meta.env.VITE_FIREBASE_APPCHECK_RECAPTCHA_ENTERPRISE_KEY || "").trim();
  if (!siteKey || typeof window === "undefined") return null;

  const debugToken = String(import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN || "").trim();
  if (debugToken) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === "true" ? true : debugToken;
  }

  try {
    return initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    console.warn("[firebase] App Check initialization skipped.", error);
    return null;
  }
}

export const appCheck = initAppCheck();
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, "us-central1");
export const storage = getStorage(app);
